import { Link } from 'react-router-dom';
import { cn } from '@verlo/shared/lib/utils';
import { tone as toneOf } from '@verlo/shared/lib/tones';

/**
 * The one KPI tile (Back Office Review, wave A): a label, a figure in mono, a delta that
 * carries a signal tone, an optional line under it. No tinted icon in a circle — the figure
 * is the tile. Replaces the six tile designs the back office had grown.
 *
 *   <Stat label="Net sales" value="£1,587" delta={{ text: '+12% vs last Tue', tone: 'ok' }} />
 *   <Stat label="Labour" value="28.4%" delta={{ text: '1.2pt over target', tone: 'warn' }} hint="Target 27%" />
 *
 * `to` makes the whole tile a link (a report's tile that opens the report).
 */
const Stat = ({ label, value, delta, hint, to, onClick, className, size = 'md', testId }) => {
  const Tag = to ? Link : onClick ? 'button' : 'div';
  const t = delta?.tone ? toneOf(delta.tone) : null;
  return (
    <Tag
      to={to}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex min-w-0 flex-col rounded-lg border border-hairline bg-white px-4 py-3 text-left',
        (to || onClick) && 'transition-colors hover:border-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo',
        className,
      )}
      data-testid={testId ? `${testId}-tile` : undefined}
    >
      <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">{label}</div>
      {/* `testId` names the figure, so a test reads the number and not the label with it. */}
      <div className={cn('mt-1 font-mono tabular-nums text-ink', size === 'lg' ? 'text-[30px] leading-none' : 'text-[24px] leading-none')} data-testid={testId}>{value ?? '—'}</div>
      {(delta || hint) && (
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
          {delta && <span className={cn('font-medium', t ? t.word : 'text-ink-soft')}>{delta.text}</span>}
          {hint && <span className="text-ink-faint">{hint}</span>}
        </div>
      )}
    </Tag>
  );
};

/** A row of Stats that sizes itself to the count (2–6). */
export const Stats = ({ children, className, ...rest }) => (
  <div className={cn('grid gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]', className)} {...rest}>{children}</div>
);

export default Stat;
